import React, { useState, useRef } from 'react';
import { pdf } from '@react-pdf/renderer';
import SuiviHeader from '../../composants/SuiviHeader/SuiviHeader';
import TabNav from '../../composants/TabNav/TabNav';
import ValidationPopUp from '../../composants/ValidationPopUp/ValidationPopUp';
import SuiviCommandePDF from '../../pages/PDF/SuiviCommandePDF';
import SuiviEntityContent from '../../composants/SuiviEntityContent/SuiviEntityContent';
import { useSuiviEntity } from '../../hooks/useSuiviEntity';
import { useSuiviEntityTabs } from '../../hooks/useSuiviEntityTabs';
import { useSuiviEntityActions } from '../../hooks/useSuiviEntityActions';
import { operationBCService } from '../../services/entityService';
import { getStatusColor, formatApiDate } from '../../utils/entityUtils';
import './SuiviOperationBC.css';
import { getLotImage } from '../../utils/getLotImage';


const SuiviOperationBC: React.FC = () => {
  const {
    entity: operation,
    setEntity: setOperation,
    selectedPrestataire,
    setSelectedPrestataire,
    techNotes,
    setTechNotes,
    loading,
    error
  } = useSuiviEntity({
    service: operationBCService, // Utiliser le service BC spécifique
    entitiesKey: 'commandes'
  });

  const {
    activeTab,
    tabs,
    commandeType,
    isClientView,
    isPrestataireView,
    getDisabledTabs,
    getInactiveTabs,
    handleTabChange
  } = useSuiviEntityTabs(operation, {
    entityType: 'operation',
    getCommandeType: () => 'BC' // Force toujours BC
  });

  const {
    isRefuseModalOpen,
    setIsRefuseModalOpen,
    isSupprimerModalOpen,
    setIsSupprimerModalOpen,
    handleRefuseEntity,
    handleDeleteEntity,
    handleFinaliserEntity,
    handleLancerConsultation
  } = useSuiviEntityActions({
    service: operationBCService, // Utiliser le service BC spécifique
    entityType: 'operation',
    redirectRoute: '/operations'
  });

  const [acceptCGV, setAcceptCGV] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const contentRef = useRef<HTMLDivElement>(null);


  const validateConsultationForm = () => {
    const newErrors = { ...errors };

    // Validation des champs obligatoires pour BC
    if (!operation?.formulaire?.nomOperation?.trim()) {
      newErrors.nomOperation = "Le nom de l'opération est obligatoire";
    }

    if (!operation?.formulaire?.besoin?.trim()) {
      newErrors.besoin = "Le besoin est obligatoire";
    }

    if (!operation?.formulaire?.adresse?.trim()) {
      newErrors.adresse = "L'adresse est obligatoire";
    }

    if (!operation?.formulaire?.codePostal?.trim()) {
      newErrors.codePostal = "Le code postal est obligatoire";
    }

    if (!operation?.formulaire?.ville?.trim()) {
      newErrors.ville = "La ville est obligatoire";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleExport = async () => {
    if (!operation) return;

    const pdfData = {
      ...operation,
      id: operation.id.toString(),
      numeroOperation: operation.numero,
      titre: operation.nom,
      lotTechnique: `${operation.lot_technique?.id} - ${operation.lot_technique?.nom}`,
      lotGeographique: `${operation.id_lot_departement?.toString()} - ${operation.lot_departement?.departement?.nom}`,
      status: operation.status?.nom,
      type: 'BC',
      dateCreation: formatApiDate(operation.created_at),
      dateValidation: formatApiDate(operation.updated_at),
      client: {
        organisme: operation.organisme_nom || '',
        contact: '',
        email: '',
        telephone: ''
      }
    };

    const blob = await pdf(<SuiviCommandePDF commande={pdfData} />).toBlob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `operation-bc-${operation.numero || operation.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="loading-indicator loading-fullpage">Chargement des détails de l'opération BC...</div>;
  }

  if (error && !operation) {
    return (
      <div className="error-message">
        <p>Erreur: {error}</p>
        <p>Impossible de charger les détails de cette opération BC.</p>
      </div>
    );
  }

  if (!operation) {
    return <div className="empty-state">Aucune opération BC trouvée avec cet identifiant.</div>;
  }


  return (
    <div className="suivi-commande" ref={contentRef}>
      <div className="no-print">
        <SuiviHeader
          titre={operation.nom}
          sousTitre={`Suivi du Bon de Commande n° ${operation.numero}`}
          subtitle={`${operation.lot_technique?.id} - ${operation.lot_technique?.nom}, ${operation.id_lot_departement} - ${operation.lot_departement?.departement?.nom}`}
          status={operation.status?.nom || 'Statut inconnu'} // Assurer une valeur par défaut
          statusColor={getStatusColor(operation.status?.nom)}
          delais={operation.expire_at}
          backgroundImage={getLotImage(operation?.lot_technique?.nom)}
          source="BC"
          statusId={operation.status?.id}
        />
      </div>

      <div className="suivi-de-commande-content">
        <TabNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          disabledTabs={getDisabledTabs()}
          inactiveTabs={getInactiveTabs()}
        />

        <div className="tab-content">
          <SuiviEntityContent
            entity={operation}
            setEntity={setOperation}
            activeTab={activeTab}
            commandeType={commandeType}
            isClientView={isClientView}
            isPrestataireView={isPrestataireView}
            selectedPrestataire={selectedPrestataire}
            setSelectedPrestataire={setSelectedPrestataire}
            techNotes={techNotes}
            setTechNotes={setTechNotes}
            errors={errors}
            setErrors={setErrors}
            acceptCGV={acceptCGV}
            setAcceptCGV={setAcceptCGV}
            handleExport={handleExport}
            validateConsultationForm={validateConsultationForm}
            handleLancerConsultation={() => handleLancerConsultation(operation)}
            handleFinaliserCommande={(id_signataire?: number, type_signataire?: string) =>
              handleFinaliserEntity(operation, techNotes, selectedPrestataire, id_signataire, type_signataire)
            }
            entityType="operation"
            printRef={contentRef}
          />
        </div>

        <ValidationPopUp
          isOpen={isRefuseModalOpen}
          title={"Refus de l'opération"}
          text="Êtes-vous sûr de vouloir refuser cette opération ?"
          onClose={() => setIsRefuseModalOpen(false)}
          onConfirm={async () => {
            handleRefuseEntity();
            setIsRefuseModalOpen(false);
          }}
          cancelLabel="Non"
          confirmLabel="Oui"
        />

        <ValidationPopUp
          isOpen={isSupprimerModalOpen}
          title={"Suppression de l'opération"}
          text="Êtes-vous sûr de vouloir supprimer cette opération ?"
          onClose={() => setIsSupprimerModalOpen(false)}
          onConfirm={() => {
            handleDeleteEntity();
            setIsSupprimerModalOpen(false);
          }}
          cancelLabel="Non"
          confirmLabel="Oui"
        />
      </div>
    </div>
  );
};

export default SuiviOperationBC;